import {
  InformationMessage,
  JourneyMessage,
  LocalizedText,
  RealtimeDeparture,
  Ref,
  ScheduledDeparture,
  StopContentBody,
} from '../../../shared/models/stop-content.model';

/** Refs (lineRef, originRef, ...) arrive as `{ ref: string }` objects. */
function refText(ref: Ref | undefined): string {
  return ref?.ref ?? '';
}

export type DepartureBoardStatus = 'on-time' | 'early' | 'late' | 'cancelled' | 'no-prediction';

export interface DepartureRow {
  departureId: string;
  line: string;
  destination: string;
  via: string;
  quay: string;
  aimedDepartureTime?: string | null;
  expectedDepartureTime?: string | null;
  delayMinutes: number | null;
  status: DepartureBoardStatus;
  isRealtimeOnly: boolean;
  isAtStop: boolean;
}

/** Picks the best-matching text for `lang`: exact match, else 'en', else the
 *  first entry, else ''. Used for messageText/publishedLineName/
 *  destinationName/via/operatorName — all modelled as LocalizedText[]. */
export function pickLocalizedText(entries: LocalizedText[] | undefined, lang: string): string {
  if (!entries || entries.length === 0) return '';
  return (
    entries.find((e) => e.lang === lang)?.text ??
    entries.find((e) => e.lang === 'en')?.text ??
    entries[0].text ??
    ''
  );
}

function delayMinutes(aimed?: string | null, expected?: string | null): number | null {
  if (!aimed || !expected) return null;
  const ms = new Date(expected).getTime() - new Date(aimed).getTime();
  if (Number.isNaN(ms)) return null;
  const minutes = Math.round(ms / 60_000);
  return minutes === 0 ? null : minutes;
}

/** Prefer an explicit status field; only derive from delay sign when both are
 *  absent. CANCELLED is only ever explicit, never derived from delay. */
function deriveStatus(
  explicitStatus: string | undefined,
  hasPrediction: boolean,
  minutes: number | null,
): DepartureBoardStatus {
  if (explicitStatus === 'CANCELLED') return 'cancelled';
  if (explicitStatus === 'LATE' || explicitStatus === 'DELAYED') return 'late';
  if (explicitStatus === 'EARLY') return 'early';
  if (explicitStatus === 'ON-TIME') return 'on-time';
  if (!hasPrediction) return 'no-prediction';
  if (minutes === null) return 'on-time';
  return minutes > 0 ? 'late' : 'early';
}

/**
 * Merges scheduled + realtime departures into one board, keyed on
 * `departureId`. A realtime departure with no scheduled twin is still a row,
 * flagged `isRealtimeOnly` — it carries its own aimed+expected times via
 * `monitoredCall`, so it's "missing a timetable entry", not "missing data".
 * Sorted by expected departure time ascending, falling back to aimed when
 * expected is absent.
 *
 * On real SIRI data the endpoint sends everything pre-merged as one
 * `departures` array (no separate scheduled half) — `scheduledDepartures` is
 * still merged in here in case a feed ever does send one.
 */
export function buildDepartureBoard(content: StopContentBody, lang: string): DepartureRow[] {
  const scheduled = content.scheduledDepartures ?? [];
  const realtime = content.departures ?? [];
  const realtimeById = new Map<string, RealtimeDeparture>();
  for (const r of realtime) realtimeById.set(r.departureId, r);

  const rows: DepartureRow[] = [];
  const seen = new Set<string>();

  for (const s of scheduled) {
    seen.add(s.departureId);
    rows.push(buildRow(s, realtimeById.get(s.departureId), lang));
  }
  for (const r of realtime) {
    if (seen.has(r.departureId)) continue;
    rows.push(buildRow(undefined, r, lang));
  }

  return rows.sort((a, b) => {
    const aTime = a.expectedDepartureTime ?? a.aimedDepartureTime ?? '';
    const bTime = b.expectedDepartureTime ?? b.aimedDepartureTime ?? '';
    return aTime.localeCompare(bTime);
  });
}

function buildRow(scheduled: ScheduledDeparture | undefined, realtime: RealtimeDeparture | undefined, lang: string): DepartureRow {
  const targetedJourney = scheduled?.targetedVehicleJourney;
  const monitoredJourney = realtime?.monitoredVehicleJourney;
  const targetedCall = targetedJourney?.targetedCall;
  const monitoredCall = monitoredJourney?.monitoredCall;

  const line =
    pickLocalizedText(monitoredJourney?.publishedLineName ?? targetedJourney?.publishedLineName, lang) ||
    refText(monitoredJourney?.lineRef ?? targetedJourney?.lineRef);
  const destination = pickLocalizedText(
    monitoredJourney?.destinationName ?? targetedJourney?.destinationName,
    lang,
  );
  const via = pickLocalizedText(monitoredJourney?.via ?? targetedJourney?.via, lang);
  const quay = (monitoredCall?.quay ?? targetedCall?.quay ?? '').replace(/^Stand\s+/i, '');

  const aimedDepartureTime = monitoredCall?.aimedDepartureTime ?? targetedCall?.aimedDepartureTime ?? null;
  const expectedDepartureTime = monitoredCall?.expectedDepartureTime ?? null;
  const hasPrediction = expectedDepartureTime != null;
  const minutes = delayMinutes(aimedDepartureTime, expectedDepartureTime);

  const explicitStatus = monitoredCall?.departureStatus ?? monitoredJourney?.progressStatus;
  const status = deriveStatus(explicitStatus, hasPrediction, minutes);

  return {
    departureId: scheduled?.departureId ?? realtime!.departureId,
    line,
    destination,
    via,
    quay,
    aimedDepartureTime,
    expectedDepartureTime,
    delayMinutes: status === 'cancelled' || !hasPrediction ? null : minutes,
    status,
    isRealtimeOnly: !scheduled && !!realtime,
    isAtStop: !!monitoredCall?.vehicleAtStop,
  };
}

/**
 * Filters to the "live" set of messages: REPLACE = current version of that
 * messageId, DELETE = tombstone (excluded entirely); later array entries win
 * for the same messageId (assumes array order = arrival order). Then keeps
 * only messages whose display window `[earliestDisplayTime, latestDisplayTime]`
 * contains `now` (missing bounds = unbounded on that side).
 */
export function mergeLiveMessages<
  T extends { messageId: string; messageAction: string; earliestDisplayTime?: string; latestDisplayTime?: string },
>(messages: T[] | undefined, now: Date = new Date()): T[] {
  if (!messages || messages.length === 0) return [];

  const latestByid = new Map<string, T>();
  for (const m of messages) {
    if (m.messageAction === 'DELETE') {
      latestByid.delete(m.messageId);
    } else {
      latestByid.set(m.messageId, m);
    }
  }

  const nowMs = now.getTime();
  return [...latestByid.values()].filter((m) => {
    const earliest = m.earliestDisplayTime ? new Date(m.earliestDisplayTime).getTime() : -Infinity;
    const latest = m.latestDisplayTime ? new Date(m.latestDisplayTime).getTime() : Infinity;
    return nowMs >= earliest && nowMs <= latest;
  });
}

/** Sorts live messages emergency-first, then by messageActionPriority
 *  descending — used for both information and journey message lists. */
export function sortMessagesByPriority<T extends { messagePriority?: string; messageActionPriority?: number }>(
  messages: T[],
): T[] {
  return [...messages].sort((a, b) => {
    const aEmergency = a.messagePriority === 'EMERGENCY' ? 0 : 1;
    const bEmergency = b.messagePriority === 'EMERGENCY' ? 0 : 1;
    if (aEmergency !== bEmergency) return aEmergency - bEmergency;
    return (b.messageActionPriority ?? 0) - (a.messageActionPriority ?? 0);
  });
}

export type { InformationMessage, JourneyMessage };
